import { QueryClient } from '@tanstack/react-query';
import { eventSchemas } from 'v4/core/events.schema';
import { SocketEvent } from 'v4/core/useSocket';
import { resourceShortlistKeys } from 'v4/entities/associations/resource__shortlist/resource__shortlist.keys';
import { shortlistInternals } from 'v4/entities/shortlist/shortlist.hooks';
import type { ReduxProxy } from 'v5/api/_common/reduxProxy';
import type { MessageService } from 'v5/services/internal/MessageService/MessageService';

export function processEventForShortlists(
  event: SocketEvent,
  client: QueryClient
) {
  if (event.type === 'SHORTLIST_UPDATED') {
    const typedEvent = eventSchemas.shortlist.updated.parse(event);
    // remove all unused shortlist queries
    client.removeQueries({
      type: 'inactive',
      queryKey: shortlistInternals.keys.all()
    });
    // invalidate shortlist list queries, they might be affected by the update
    client.invalidateQueries({
      queryKey: shortlistInternals.keys.lists()
    });
    // invalidate shortlist detail query
    client.invalidateQueries({
      queryKey: shortlistInternals.keys.detail(typedEvent.payload.shortlistId)
    });
  }
  return event;
}

/**
 * A write through the v5 shortlist services (adding people, removing, moving them) reports a
 * system effect. Its socket event, `SHORTLIST_BLOCKS_UPDATED`, is not one this cache reads, and
 * comes back with this tab's session anyway: the v4 shortlist reads again from the effect.
 */
export function reconcileShortlistsWithSystemEffects(
  client: QueryClient,
  messageService: MessageService
) {
  messageService.reportSystemEffect.subscribeToRequest(event => {
    for (const effect of event.request.scope) {
      if (effect.entity === 'shortlist') {
        shortlistInternals.invalidateDetails(client, effect.entityId);
      }
    }
    event.sendResponse();
  });
}

/**
 * Merging duplicates is a v1-only write that moves the merged-away person's blocks to the kept
 * one, on shortlists it doesn't name, with no socket event: every v4 shortlist and every
 * resource ↔ shortlist association reads again.
 */
export function reconcileShortlistsWithMergedResources(
  client: QueryClient,
  reduxProxy: Pick<ReduxProxy, 'read'>
) {
  reduxProxy.read.addListener(event => {
    switch (event.type) {
      case 'MERGE_RESOURCE_SUCCESS':
        client.removeQueries({
          type: 'inactive',
          queryKey: shortlistInternals.keys.all()
        });
        void client.invalidateQueries({
          type: 'active',
          queryKey: shortlistInternals.keys.all()
        });
        client.removeQueries({
          type: 'inactive',
          queryKey: resourceShortlistKeys.all()
        });
        void client.invalidateQueries({
          type: 'active',
          queryKey: resourceShortlistKeys.all()
        });
    }
  });
}
